import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './ProjectReveal.css';

/* The focused card doesn't route to a new page — it becomes the page. A white
   surface grows out of the card's exact on-screen rect into the full viewport
   (the same dot → card morph language the orbit already speaks, one step
   further: card → room), the hero image rides along from the card into its
   seat in the case study, and the copy rises in once the surface has landed.
   Closing runs the film backwards: copy fades, the image swoops back, the
   room shuts into the card it grew from — which is still sitting there on
   the orbit underneath, untouched.

   The surface is laid out fullscreen the whole time; only its clip-path
   animates. So nothing inside ever reflows mid-flight — the page is simply
   revealed as the window opens — and the hero handoff is a pure-transform
   FLIP of a fixed-size layer. Both mattes are white and the image is
   contained, so any aspect mismatch between the card and the hero slot hides
   itself in white-on-white. */

type Stage = 'closed' | 'pre-open' | 'opening' | 'open' | 'pre-close' | 'closing';

const OPEN_MS = 640;   /* matches the surface/carried transition in the CSS */
const CLOSE_MS = 660;  /* 140ms hold (outline retracts) + 520ms sweep — see CSS */
const PLAIN_MS = 260;  /* the no-morph fade (deep links, cross-page history) */

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/* Where the focused card sits on the home orbit — the same geometry Orbit
   derives from the viewport each frame. Replicating it here lets the surface
   grow out of (and shut back into) the card without reaching into the
   orbit's DOM, and stays correct across resizes because it's recomputed at
   every departure and landing. */
const focusedCardRect = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const height = clamp(Math.min(vh * 0.7, vw * 1.05), 200, 620);
  const width = clamp(Math.min(height * 1.5, vw * 0.86), 220, 940);
  return { left: (vw - width) / 2, top: (vh - height) / 2, width, height };
};

const setClip = (el: HTMLElement, value: string) => {
  el.style.clipPath = value;
  el.style.setProperty('-webkit-clip-path', value);
};

/* Scale that fits the image inside a box at its natural aspect (object-fit:
   contain). The FLIP scales the carried layer so the *contained image rects*
   coincide — the white letterboxing around them can differ freely. */
const fitScale = (boxW: number, boxH: number, natW: number, natH: number) =>
  Math.min(boxW / natW, boxH / natH);

const prefersReduced = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface ProjectRevealProps {
  open: boolean;
  /* True when the home orbit is on screen beneath us with the tapped card
     centred — the expansion continues from that card. False (deep link,
     history jump from another page) falls back to a plain fade. */
  fromCard: boolean;
  heroSrc: string;
  label: string;
  onClose: () => void;
  children: React.ReactNode;
}

const ProjectReveal: React.FC<ProjectRevealProps> = ({
  open,
  fromCard,
  heroSrc,
  label,
  onClose,
  children,
}) => {
  /* Mounted already open (a deep link): appear settled, no theatrics. */
  const [stage, setStage] = useState<Stage>(open ? 'open' : 'closed');
  const [morph, setMorph] = useState(false);  /* this open/close animates from the card */
  const [flip, setFlip] = useState(false);    /* the hero layer is riding along */

  const surfaceRef = useRef<HTMLDivElement>(null);
  const carriedRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const prevOpen = useRef(open);
  const closeFrom = useRef<DOMRect | null>(null);

  /* The hero's natural size, cached ahead of the morph. The in-page <img>
     mounts in the same frame the flight is measured, and a just-mounted img
     can still report naturalWidth 0 even for a cached file (Safari
     especially) — which used to silently skip the flight and teleport the
     image into its seat. This component is mounted from app startup, so the
     probe resolves long before a card can be tapped. */
  const natural = useRef<{ w: number; h: number } | null>(null);
  useEffect(() => {
    natural.current = null;
    const probe = new Image();
    probe.onload = () => {
      natural.current = { w: probe.naturalWidth, h: probe.naturalHeight };
    };
    probe.src = heroSrc;
    return () => {
      probe.onload = null;
    };
  }, [heroSrc]);

  const heroEl = () =>
    innerRef.current?.querySelector<HTMLImageElement>('[data-reveal-hero]') ?? null;

  useEffect(() => {
    if (open === prevOpen.current) return;
    prevOpen.current = open;
    if (open) {
      const m = fromCard && !prefersReduced();
      setMorph(m);
      setFlip(false);
      setStage('pre-open');
    } else {
      setStage((s) => (s === 'closed' ? 'closed' : 'pre-close'));
    }
  }, [open, fromCard]);

  /* The animation driver. `pre-*` stages pin the start state inline (with
     transitions suppressed) and yield two frames so it paints; the following
     stage hands transitions back to the CSS and writes the target values. */
  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    const carried = carriedRef.current;

    if (stage === 'pre-open') {
      if (surface && morph) {
        surface.scrollTop = 0;
        surface.style.overflow = '';
        surface.style.transition = 'none';
        const cr = focusedCardRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        setClip(
          surface,
          `inset(${cr.top}px ${vw - cr.left - cr.width}px ${vh - cr.top - cr.height}px ${cr.left}px round 16px)`
        );
        const hero = heroEl();
        const nat = natural.current;
        if (carried && hero && nat) {
          const hr = hero.getBoundingClientRect();
          const s =
            fitScale(cr.width, cr.height, nat.w, nat.h) /
            fitScale(hr.width, hr.height, nat.w, nat.h);
          const dx = cr.left + cr.width / 2 - (hr.left + hr.width / 2);
          const dy = cr.top + cr.height / 2 - (hr.top + hr.height / 2);
          carried.style.transition = 'none';
          carried.style.left = `${hr.left}px`;
          carried.style.top = `${hr.top}px`;
          carried.style.width = `${hr.width}px`;
          carried.style.height = `${hr.height}px`;
          carried.style.transform = `translate(${dx}px, ${dy}px) scale(${s.toFixed(5)})`;
          setFlip(true);
        }
        void surface.offsetWidth; /* commit the start state before transitioning */
      }
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setStage('opening'));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    if (stage === 'opening') {
      if (surface && morph) {
        surface.style.transition = '';
        setClip(surface, 'inset(0px 0px 0px 0px round 0px)');
        if (carried && flip) {
          carried.style.transition = '';
          carried.style.transform = 'translate(0px, 0px) scale(1)';
        }
      }
      const t = setTimeout(() => setStage('open'), morph ? OPEN_MS : PLAIN_MS);
      return () => clearTimeout(t);
    }

    if (stage === 'open') {
      if (surface) {
        surface.style.transition = '';
        setClip(surface, '');
        surface.style.overflow = '';
      }
      closeRef.current?.focus({ preventScroll: true });
      return;
    }

    if (stage === 'pre-close') {
      if (surface) {
        surface.style.overflow = 'hidden'; /* freeze the scroll where it is */
        surface.style.transition = 'none';
        setClip(surface, 'inset(0px 0px 0px 0px round 0px)');
      }
      const hero = heroEl();
      if (morph && carried && hero && natural.current) {
        /* Wherever the hero is right now — scrolled half off-screen is fine;
           it simply swoops back into the card from there. */
        const hr = hero.getBoundingClientRect();
        closeFrom.current = hr;
        carried.style.transition = 'none';
        carried.style.left = `${hr.left}px`;
        carried.style.top = `${hr.top}px`;
        carried.style.width = `${hr.width}px`;
        carried.style.height = `${hr.height}px`;
        carried.style.transform = 'translate(0px, 0px) scale(1)';
        setFlip(true);
      } else {
        setFlip(false);
      }
      if (surface) void surface.offsetWidth;
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setStage('closing'));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }

    if (stage === 'closing') {
      if (surface && morph) {
        const cr = focusedCardRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        surface.style.transition = '';
        setClip(
          surface,
          `inset(${cr.top}px ${vw - cr.left - cr.width}px ${vh - cr.top - cr.height}px ${cr.left}px round 16px)`
        );
        const hr = closeFrom.current;
        const nat = natural.current;
        if (carried && flip && hr && nat) {
          const s =
            fitScale(cr.width, cr.height, nat.w, nat.h) /
            fitScale(hr.width, hr.height, nat.w, nat.h);
          const dx = cr.left + cr.width / 2 - (hr.left + hr.width / 2);
          const dy = cr.top + cr.height / 2 - (hr.top + hr.height / 2);
          carried.style.transition = '';
          carried.style.transform = `translate(${dx}px, ${dy}px) scale(${s.toFixed(5)})`;
        }
      }
      const t = setTimeout(() => setStage('closed'), morph ? CLOSE_MS : PLAIN_MS);
      return () => clearTimeout(t);
    }
  }, [stage, morph, flip]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (stage === 'closed') return null;

  const classes = [
    'project-reveal',
    `project-reveal--${stage}`,
    morph ? '' : 'project-reveal--plain',
    flip ? '' : 'project-reveal--no-flip',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} role="dialog" aria-modal="true" aria-label={label}>
      <div ref={surfaceRef} className="project-reveal-surface">
        <button
          ref={closeRef}
          className="project-reveal-close"
          onClick={onClose}
          aria-label="Close project"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M2 2 L14 14 M14 2 L2 14"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
          <svg className="project-reveal-close-ring" viewBox="0 0 50 50" aria-hidden="true">
            <circle cx="25" cy="25" r="24" />
          </svg>
        </button>
        <div ref={innerRef} className="project-reveal-inner">
          {children}
        </div>
        <div ref={carriedRef} className="project-reveal-carried" aria-hidden="true">
          <img src={heroSrc} alt="" draggable={false} decoding="sync" />
        </div>
      </div>
    </div>
  );
};

export default ProjectReveal;
